import _, { sortBy } from 'lodash';
import { i18nProps, LocalItem, Station, TicketMessage } from '~Types';
import { StorageData } from '~Components/Storage';
import { PrintRow, Alignment } from '~Components/PrinterManager';
import { getBongData_categories } from './__queries__';
import { Printer } from '~reducers';
import { DateTime } from 'luxon';
import { formatTicketMessage } from '~utilities';
import { combineArticles } from '~Utils/combineArticles';
import { TicketOrderInfo } from '.';
import {
  getCompany_Venue_company_stations_categories,
  getCompany_Venue_company_stations_items,
} from '~Scenes/Venue/__queries__';

// THIS FILE WILL BE DEPRECATED SOON

const DEFAULT_FONT_SIZE = 14;

const pad = (spacing = 0) => ' '.repeat(spacing);
const lineBreak: PrintRow = { content: ' ', fontSize: DEFAULT_FONT_SIZE };
export const divider: PrintRow[] = [
  { content: ' ', fontSize: DEFAULT_FONT_SIZE, alignment: 'LEFT' },
  { content: '───────────────────────────────────', alignment: 'LEFT' },
  { content: ' ', fontSize: DEFAULT_FONT_SIZE, alignment: 'LEFT' },
];

const getPrintItem = ({
  text,
  spacing = 0,
  fontSize = DEFAULT_FONT_SIZE,
  breakLine,
  alignment,
}: {
  text: string;
  spacing?: number;
  fontSize?: number;
  breakLine?: boolean;
  alignment?: Alignment;
}): PrintRow | null => {
  // Safeguard to ensure text is not undefined
  if (!text) {
    return null; // If text is undefined or empty, return null to be filtered later
  }

  return {
    content: `${pad(spacing)}${text}${breakLine ? '\n' : ''}`,
    fontSize,
    alignment: alignment || 'LEFT',
  };
};

// Group items together by count, example 2x item1, 1x item2
const groupItems = ({
  items,
  stationItemIds,
  showAllItems, // Shows items not included in the station
  storage,
  i18n,
  formatCurrency,
}: {
  items: LocalItem[];
  stationItemIds: string[];
  showAllItems: boolean;
  storage: StorageData;
  i18n: i18nProps;
  formatCurrency: (number: number) => string;
}): PrintRow[] => {
  let itemsCombined = combineArticles(items);

  // Sort items alphabetically by title if the setting is enabled
  if (storage.bongSortItemsAlphabetically) {
    itemsCombined = sortBy(itemsCombined, (item) => item.title.toLowerCase());
  }

  const itemContents = itemsCombined.map((item) => {
    const price = (item.amount ?? 0) - (item.discount ?? 0);
    const priceString = formatCurrency(price);

    // If the item is in the station we print it in large font
    if (stationItemIds.includes(item.itemId)) {
      return printItem(item, true, storage, i18n, formatCurrency, item.count, priceString);
    }

    // If the setting showAllItems is toggled off, we dont print any items in small font.
    if (showAllItems === false) {
      return null;
    }

    // If the item has a coursetype print it in small font
    if (item.courseType?.id) {
      return printItem(item, false, storage, i18n, formatCurrency, item.count, priceString);
    }

    return null;
  });

  return itemContents
    ? itemContents
        .flat()
        .filter((item): item is PrintRow => item !== null && typeof item !== 'string')
    : ([] as PrintRow[]);
};

// Print template for title, date, service type, estimated time, desired time, delivery location
export function printHeader(
  {
    title,
    date,
    serviceType,
    estimatedTime,
    desiredTime,
    deliveryLocation,
    coverage,
    floorplan,
    tableId,
  }: TicketOrderInfo,
  storage: StorageData,
  i18n: i18nProps,
  staffName?: string,
  deviceName?: string,
): PrintRow[] {
  const serviceTypeName = !i18n(`Bong.${serviceType}`).includes('Missing translation')
    ? i18n(`Bong.${serviceType}`)
    : serviceType;

  /**
   * This ID is used to identify duplicate prints.
   */
  const printId = Math.round(Math.random() * 100)
    .toString()
    .padStart(3, '0');

  return [
    lineBreak,
    lineBreak,
    storage.BongShowPrintId &&
      getPrintItem({ fontSize: 12, alignment: 'RIGHT', text: `${printId}` }),
    lineBreak,
    lineBreak,
    lineBreak,
    lineBreak,
    lineBreak,
    lineBreak,
    // Title ex. #320 or Table 3
    title &&
      getPrintItem({
        text: title,
        fontSize: 35,
      }),
    storage.bongShowFloorplan && floorplan && getPrintItem({ text: `${floorplan}` }),
    // Incoming date and time
    (!desiredTime || !storage.BongHideAllExceptDesiredTime) &&
      getPrintItem({
        text: DateTime.fromISO(date).toFormat('HH:mm yyyy-MM-dd'),
      }),
    // Service type
    !storage.hideServiceType &&
      !tableId &&
      getPrintItem({
        text: serviceTypeName,
        fontSize: 28,
      }),
    // Staff name
    staffName &&
      getPrintItem({
        text: `${i18n('Bong.Waiter')}: ${staffName}`,
      }),
    // Number of guests on table
    coverage &&
      isFinite(coverage) &&
      getPrintItem({
        text: `${i18n('Bong.Coverage')}: ${coverage}`,
      }),
    deviceName &&
      getPrintItem({
        text: `${i18n('Bong.Device')}: ${deviceName}`,
      }),
    // Estimated finish time
    estimatedTime &&
      !storage.BongHideAllExceptDesiredTime &&
      getPrintItem({
        text: `${i18n('Bong.EstFinishTime')}: ${DateTime.fromISO(estimatedTime).toFormat(
          'HH:mm yyyy-MM-dd',
        )}`,
      }),
    // Desired time
    desiredTime &&
      getPrintItem({
        text: `${i18n('Bong.DesiredTime')}: ${DateTime.fromISO(desiredTime).toFormat(
          'HH:mm yyyy-MM-dd',
        )}`,
      }),
    // Delivery Location
    deliveryLocation &&
      getPrintItem({
        text: `${i18n('Bong.DeliveryLocation')}: ${deliveryLocation}`,
      }),
  ].filter((item): item is PrintRow => item !== null && typeof item !== 'string');
}

// Groups items by category and display them in sections, they are always printed on the same ticket, and always only items relevant to stations.
// Items with course types are never grouped by category, so they will not be in the items.
export function printCategoryGroups({
  items,
  categories = [],
  i18n,
  stationItemIds,
  storage,
  formatCurrency,
}: {
  items: LocalItem[];
  categories: getBongData_categories[];
  i18n: i18nProps;
  stationItemIds: string[];
  storage: StorageData;
  formatCurrency: (number: number) => string;
}): PrintRow[] {
  const groupedItems = _.groupBy(items, 'categoryId');

  const printPayload: (PrintRow | null)[] = [];

  Object.entries(groupedItems).forEach(([gcategoryId, gitems]) => {
    const category = categories.find((c) => c.id === gcategoryId);
    const printItems = groupItems({
      items: gitems,
      stationItemIds,
      showAllItems: false, // Group by category has nothing to do with coursetypes so we have false here. Show all items is for showing other course items in small font
      storage,
      i18n,
      formatCurrency,
    });

    if (category) {
      printPayload.push(
        getPrintItem({
          text: i18n(category.nameLang),
          fontSize: 22,
        }),
      );

      printPayload.push(...printItems);

      printPayload.push(...divider);
    }
  });

  return printPayload.filter((row): row is PrintRow => row !== null && typeof row !== 'string');
}

// Print template for course group and corresponding items
export function printCourseGroup({
  courseName,
  items,
  storage,
  stationItemIds = [],
  showAllItems,
  useDivider = true,
  i18n,
  formatCurrency,
}: {
  courseName: string;
  items: LocalItem[];
  storage: StorageData;
  stationItemIds: string[];
  showAllItems: boolean; // Show items not included in the station, this setting is on station and configured in QO
  useDivider: boolean;
  i18n: i18nProps;
  formatCurrency: (number: number) => string;
}): PrintRow[] {
  const content = groupItems({
    items,
    stationItemIds,
    showAllItems,
    storage,
    i18n,
    formatCurrency,
  });

  // If there is no content (due to showAllItems being false) we don't want to print the course type title
  if (content.length === 0) {
    return [];
  }

  const printPayload = [
    courseName &&
      getPrintItem({
        text: `${courseName}`,
        fontSize: 22,
      }),
    ...content,
  ];

  if (useDivider) printPayload.push(...divider);

  return printPayload.filter(
    (item): item is PrintRow => item !== null && typeof item !== 'string',
  );
}

// Print template for messages
export function printMessage(messages: string[], useTopDivider = false): PrintRow[] {
  const topDivider = useTopDivider ? divider : [];
  return [
    ...topDivider,
    ...messages.map((message) =>
      getPrintItem({
        text: message,
        fontSize: 28,
      }),
    ),
    ...divider,
  ].filter((item): item is PrintRow => item !== null);
}

// Print template for order comment
export function printOrderComment(comment: string, useTopDivider = false): PrintRow[] {
  const topDivider = useTopDivider ? divider : [];

  const printRow = getPrintItem({
    text: comment,
    fontSize: 14,
  });

  return [...topDivider, printRow, ...divider].filter((item): item is PrintRow => item !== null);
}

// Print template for item title, variations, removed variations and message
// Highlight is used to print the item in large font, Highlight: false is used to print course items from other stations in a small font.
export function printItem(
  item: LocalItem,
  highlight: boolean,
  storage: StorageData,
  i18n: i18nProps,
  formatCurrency: (number: number) => string,
  count?: number,
  price?: string,
): PrintRow[] {
  const icon = highlight ? '➤' : '';
  const variations = item.variations.map((variation: any) =>
    getPrintItem({
      text: `${variation.name} ${storage.bongShowPrices ? formatCurrency(variation.price) : ''}`,
      spacing: 6,
      fontSize: 14,
    }),
  );

  const removedVariations = item.removedVariations.map((variation: any) =>
    getPrintItem({
      text: `${i18n('Bong.Remove')}: ${variation.name}`,
      spacing: 6,
      fontSize: 14,
    }),
  );

  const title =
    highlight && !storage.BongDisableCapitalLetters
      ? item.title.toUpperCase() + (storage.bongShowPrices ? ' ' + price : '')
      : item.title + (storage.bongShowPrices ? ' ' + price : '');

  return [
    getPrintItem({
      text: `${icon} ${count ? count : item.count}x ${title}`,
      fontSize: highlight ? 20 : DEFAULT_FONT_SIZE,
      spacing: highlight ? 0 : 1,
    }),
    ...variations,
    ...removedVariations,
    item.message &&
      getPrintItem({
        text: item.message,
        spacing: 8,
        fontSize: 14,
      }),
  ].filter((item): item is PrintRow => item !== null && typeof item !== 'string');
}

export function printExtraSpace(): PrintRow[] {
  return [lineBreak, lineBreak, lineBreak, lineBreak];
}

export function printTestContent(
  printer: any,
  stations: any[],
  items: getCompany_Venue_company_stations_items[],
  categories: getCompany_Venue_company_stations_categories[],
  i18n: i18nProps,
  deviceName?: string,
): PrintRow[] {
  const stationNames = stations.map((s) => s.name).join(', ');
  return [
    ...printExtraSpace(),
    getPrintItem({ text: i18n('Bong.TestPrint'), fontSize: 20 }),
    getPrintItem({ text: `${i18n('Bong.Device')}: ${deviceName}`, fontSize: 12 }),
    getPrintItem({
      text: `${i18n('Bong.Printer.Id')}: ${printer.id} | ${i18n('Bong.Printer.Model')}: ${
        printer.model
      }`,
      fontSize: 12,
    }),
    getPrintItem({ text: `${i18n('Bong.Stations')}: ${stationNames}`, fontSize: 12 }),
    getPrintItem({ text: '------' }),
    lineBreak,
    getPrintItem({ text: i18n('Bong.Printer.Items'), fontSize: 12 }),
    ...items.map((i) => getPrintItem({ text: i.title, fontSize: 10 })),
    lineBreak,
    lineBreak,
    getPrintItem({ text: i18n('Bong.Printer.Categories'), fontSize: 12 }),
    ...categories.map((c) => getPrintItem({ text: c.name, fontSize: 10 })),
  ].filter((item): item is PrintRow => item !== null);
}

export const processGroupedStationItems = ({
  typeGrouped,
  storage,
  categories,
  stationItemIds,
  i18n,
  currency,
  printerStations,
}: {
  typeGrouped: _.Dictionary<LocalItem[]>;
  storage: StorageData;
  categories: getBongData_categories[];
  stationItemIds: string[];
  i18n: i18nProps;
  currency: {
    format: (amount: number, divide?: boolean) => string;
  };
  printerStations: Station[];
}) => {
  return Object.keys(typeGrouped).map((key) => {
    // key === 'noType' is used for items without courseType
    if (key === 'noType') {
      typeGrouped[key] = typeGrouped[key].filter((courseItem: any) =>
        stationItemIds.includes(courseItem.itemId),
      );
    }

    // If there are no items in this group, don't print anything
    if (typeGrouped[key].length === 0) {
      return [];
    }

    // For the group without courseType, we print category groups if the setting is on otherwise we use printCourseGroup (even though it is not a course group)
    if (key === 'noType' && storage.bongGroupByCategory) {
      return printCategoryGroups({
        categories,
        items: typeGrouped[key],
        i18n,
        stationItemIds,
        storage,
        formatCurrency: currency.format,
      });
    } else {
      // Default is to show all items from other stations that is why null and undefined is considered true
      // To not show items from other stations, the setting showAllItems must be set to false in QO
      const shouldShowItemsFromOtherStations = printerStations.some(
        (station) =>
          station.showAllItems ||
          station.showAllItems === undefined ||
          station.showAllItems === null,
      );

      return printCourseGroup({
        courseName: key === 'noType' ? '' : key,
        items: typeGrouped[key],
        storage,
        stationItemIds,
        showAllItems: shouldShowItemsFromOtherStations,
        useDivider: true,
        i18n,
        formatCurrency: currency.format,
      });
    }
  });
};

export const generatePrintContent = ({
  header,
  stationName,
  messages,
  groupedStationItems,
  orderComment,
  ticketMessages,
  i18n,
}: {
  header: PrintRow[];
  stationName?: string;
  messages?: LocalItem[];
  groupedStationItems: PrintRow[][];
  orderComment: PrintRow[];
  ticketMessages: TicketMessage[];
  i18n: i18nProps;
}) => {
  const printContent = [...header];

  // We don't need to print a divider if there is no station name
  if (!stationName) {
    printContent.push(...divider);
  }

  // Station name shows when we have the setting bongPrintByStation true ("Prints a separate batch for each station on the printer").
  if (stationName) {
    printContent.push(...stationTitle(i18n, stationName));
  }

  if (messages && messages.length > 0) {
    printContent.push(
      ...printMessage(messages.map((m) => `${m.courseType ? m.courseType.label : ''} ${m.title}`)),
    );
  }

  if (groupedStationItems.length > 0) {
    printContent.push(...groupedStationItems.flat());
  }

  printContent.push(...formatTicketMessages({ ticketMessages }).flat());

  printContent.push(...orderComment);
  printContent.push(...printExtraSpace());
  return printContent;
};

// The character '➤' is not supported by all printers, so we remove it for Epson printers which otherwise would print a '?' instead
export const removeSpecialCharactersForEpson = ({
  printer,
  printContent,
}: {
  printer: Printer;
  printContent: PrintRow[];
}) => {
  if (
    printer.model.toLowerCase().startsWith('tm') ||
    printer.model.toLowerCase().startsWith('epson')
  ) {
    printContent.forEach((line) => {
      if (line && line.content) {
        line.content = line.content.replace('➤', '');
      }
    });
  }
};

export const courseTypeMessages = ({
  typeGroupedMessages,
  courseType,
}: {
  typeGroupedMessages: _.Dictionary<LocalItem[]>;
  courseType: string;
}) => {
  // Print messages without courseType and with current courseType
  const messageContent = [];
  const relevantMessages = [
    ...(typeGroupedMessages['noType'] ? typeGroupedMessages['noType'] : []),
    ...(typeGroupedMessages[courseType] ? typeGroupedMessages[courseType] : []),
  ];

  if (relevantMessages && relevantMessages.length > 0) {
    messageContent.push(
      printMessage(
        relevantMessages?.map((m) => `${m.courseType ? m.courseType.label : ''} ${m.title}`),
      ),
    );
  }

  return messageContent;
};

export const formatTicketMessages = ({
  ticketMessages,
  courseTypeLabel,
  useTopDivider = false,
}: {
  ticketMessages: TicketMessage[];
  courseTypeLabel?: string;
  useTopDivider?: boolean;
}) => {
  const messageContent: PrintRow[] = [];

  const messagesWithoutCourseType = ticketMessages
    .filter((tm) => !tm.courseId)
    .map((tm) => tm.message);
  const messagesWithCourseType = ticketMessages
    .filter((tm) => tm.courseId)
    .map((tm) => tm.message);

  if (messagesWithoutCourseType.length > 0) {
    messageContent.push(...printMessage(messagesWithoutCourseType, useTopDivider));
  }

  if (messagesWithCourseType.length > 0) {
    const formattedMessages = messagesWithCourseType.map((message) => {
      return formatTicketMessage(message, courseTypeLabel);
    });
    messageContent.push(...printMessage(formattedMessages, useTopDivider));
  }

  return messageContent;
};

export const courseGroupContent = ({
  courseItems,
  courseType,
  stationItemIds,
  storage,
  categories,
  i18n,
  currency,
  printerStations,
}: {
  courseItems: any[];
  courseType: string;
  stationItemIds: string[];
  storage: any;
  categories: any[];
  i18n: any;
  currency: any;
  printerStations: any[];
}) => {
  // If there are no relevant items in this courseType, don't print
  if (!courseItems.some((ci) => stationItemIds.includes(ci.itemId))) {
    return [];
  }

  // If courseType is null ('noType'), only print relevant station items
  if (courseType === 'noType') {
    courseItems = courseItems.filter((i) => stationItemIds.includes(i.itemId));
  }

  // Sort items by if they are relevant for station, and optionally alphabetically
  if (storage.bongSortItemsAlphabetically) {
    // Sort by station relevance first, then alphabetically within each group
    courseItems = sortBy(courseItems, [
      (i) => !stationItemIds.includes(i.itemId),
      (i) => i.title.toLowerCase(),
    ]);
  } else {
    // Only sort by station relevance
    courseItems = sortBy(courseItems, (i) => !stationItemIds.includes(i.itemId));
  }

  let content: PrintRow[] = [];

  // If the courseType is 'noType' and setting to grouping by category is on, print category groups otherwise it uses printCourseGroup (even though it is not a course group)
  if (courseType === 'noType' && storage.bongGroupByCategory) {
    content = printCategoryGroups({
      categories,
      items: courseItems,
      i18n,
      stationItemIds,
      storage,
      formatCurrency: currency.format,
    });
  } else {
    // Default is to show all items from other stations that is why null and undefined is considered true
    // To not show items from other stations, the setting showAllItems must be set to false in QO on the station
    const shouldShowItemsFromOtherStations = printerStations.some(
      (station) =>
        station.showAllItems ||
        station.showAllItems === undefined ||
        station.showAllItems === null,
    );

    content = printCourseGroup({
      courseName: courseType === 'noType' ? '' : courseType,
      items: courseItems,
      storage,
      stationItemIds,
      showAllItems: shouldShowItemsFromOtherStations,
      useDivider: false,
      i18n,
      formatCurrency: currency.format,
    });
  }

  return content ?? [];
};

export const stationTitle = (i18n: i18nProps, stationName?: string) => {
  const title = getPrintItem({ text: i18n('Bong.Station') + ': ' });
  const nameObj = getPrintItem({ text: stationName ?? '', fontSize: 22 });

  if (!nameObj || !title) {
    return [];
  }

  return [lineBreak, title, nameObj, ...divider];
};
